import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { LogOut, User, Settings, ChevronDown, ClipboardList, KeyRound, BookOpen, BarChart3, CalendarCheck, Mail, Stethoscope, Calculator } from 'lucide-react';
import ChangePasswordModal from '../ChangePasswordModal';
import { canOpenPlaybook } from '../playbook/playbookRole';
import { useFilterContext } from './FilterContext';

const ROLE_DOT = {
  superadmin:  'bg-purple-500',
  stakeholder: 'bg-orange-500',
  sales:       'bg-blue-500',
  physician:   'bg-emerald-500',
};

const ROLE_TEXT = {
  superadmin:  'text-purple-600',
  stakeholder: 'text-orange-600',
  sales:       'text-blue-600',
  physician:   'text-emerald-600',
};

const Header = ({ user, onLogout }) => {
  const navigate              = useNavigate();
  const location              = useLocation();
  const { isFilterOpen }      = useFilterContext();
  const [open, setOpen]       = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [pos,  setPos]        = useState({ top: 0, right: 0 });
  const triggerRef            = useRef(null);

  const handleLogout = async () => {
    setOpen(false);
    await onLogout();
    navigate('/login');
  };

  // Calculate dropdown position from trigger button
  const handleToggle = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({
        top:   rect.bottom + window.scrollY + 6,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen(prev => !prev);
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close on scroll
  useEffect(() => {
    const handler = () => setOpen(false);
    window.addEventListener('scroll', handler, true);
    return () => window.removeEventListener('scroll', handler, true);
  }, []);

  const dropdown = open && createPortal(
    <div
      style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: 99999 }}
      className="w-52 bg-white rounded-xl shadow-xl border border-gray-200 py-1.5"
      onMouseDown={e => e.stopPropagation()}
    >
      {/* User info */}
      <div className="px-4 py-2.5 border-b border-gray-100">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Signed in as</p>
        <p className="text-sm font-medium text-gray-800 mt-0.5 truncate">{user?.name}</p>
        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
      </div>

      {/* CAR-T Playbook - physician, superadmin, stakeholder, Medical Advisor */}
      {canOpenPlaybook(user) && (
        <button
          onClick={() => { setOpen(false); navigate('/playbook'); }}
          className="w-full flex items-center space-x-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <Stethoscope className="w-4 h-4 text-gray-400" />
          <span>CAR-T Playbook</span>
        </button>
      )}

      {/* IPI Calculator - Sales users and superadmin (feedback #13) */}
      {(user?.role === 'sales' || user?.role === 'superadmin') && (
        <button
          onClick={() => { setOpen(false); navigate('/ipi-calculator'); }}
          className="w-full flex items-center space-x-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <Calculator className="w-4 h-4 text-gray-400" />
          <span>IPI Calculator</span>
        </button>
      )}

      {/* User Management - superadmin only */}
      {user?.role === 'superadmin' && (
        <button
          onClick={() => { setOpen(false); navigate('/user-management'); }}
          className="w-full flex items-center space-x-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <Settings className="w-4 h-4 text-gray-400" />
          <span>User Management</span>
        </button>
      )}

      {/* Audit Logs — superadmin only */}
      {user?.role === 'superadmin' && (
        <button
          onClick={() => { setOpen(false); navigate('/audit-logs'); }}
          className="w-full flex items-center space-x-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <ClipboardList className="w-4 h-4 text-gray-400" />
          <span>Audit Logs</span>
        </button>
      )}

      {/* Engagement Report — superadmin only */}
      {user?.role === 'superadmin' && (
        <button
          onClick={() => { setOpen(false); navigate('/engagement-report'); }}
          className="w-full flex items-center space-x-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <BarChart3 className="w-4 h-4 text-gray-400" />
          <span>Engagement Report</span>
        </button>
      )}

      {/* Email Campaigns — superadmin only */}
      {user?.role === 'superadmin' && (
        <button
          onClick={() => { setOpen(false); navigate('/email-campaigns'); }}
          className="w-full flex items-center space-x-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <Mail className="w-4 h-4 text-gray-400" />
          <span>Email Campaigns</span>
        </button>
      )}

      {/* Patient Milestones — superadmin + stakeholder */}
      {(user?.role === 'superadmin' || user?.role === 'stakeholder') && (
        <button
          onClick={() => { setOpen(false); navigate('/patient-milestones'); }}
          className="w-full flex items-center space-x-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <CalendarCheck className="w-4 h-4 text-gray-400" />
          <span>Patient Milestones</span>
        </button>
      )}

      {/* Glossary — commented out per client request
      <button
        onClick={() => { setOpen(false); navigate('/glossary'); }}
        className="w-full flex items-center space-x-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <BookOpen className="w-4 h-4 text-gray-400" />
        <span>Glossary</span>
      </button>
      */}

      {/* Change Password — all roles */}
      <button
        onClick={() => { setOpen(false); setShowChangePwd(true); }}
        className="w-full flex items-center space-x-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
      >
        <KeyRound className="w-4 h-4 text-gray-400" />
        <span>Change Password</span>
      </button>

      {/* <div className="border-t border-gray-100 my-1" /> */}

      {/* Logout */}
      <button
        onClick={handleLogout}
        className="w-full flex items-center space-x-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
      >
        <LogOut className="w-4 h-4" />
        <span>Logout</span>
      </button>
    </div>,
    document.body
  );

  return (
    <header className="bg-white shadow-sm border-b-2 border-emerald-600 top-0 z-40">
      <div className="max-w-[1390px] mx-auto px-6 py-2">
        <div className={`flex items-center justify-between transition-all duration-300 ${isFilterOpen ? 'ml-64 lg:ml-[332px]' : 'ml-[82px]'}`}>

          {/* Logo */}
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex justify-center items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
              aria-label="Go to dashboard"
            >
              <img src="/Original.svg" alt="Actelligence Logo" className="h-9 w-auto" />
              <h1 className="text-[28px] font-bold bg-gradient-to-r from-[#355699] via-[#307a78] to-[#86be22] bg-clip-text text-transparent h-11">
                ACT<span className="italic">elligence</span>
              </h1>
            </button>
          </div>

          {/* User trigger */}
          {user && (
            <button
              ref={triggerRef}
              onClick={handleToggle}
              className="flex items-center space-x-2 px-2 py-1 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
            >
              <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
              {user.role && (
                <div className="flex items-center space-x-1.5">
                  {/* <span className={`w-2 h-2 rounded-full flex-shrink-0 ${ROLE_DOT[user.role] || 'bg-gray-400'}`} /> */}
                  <span className={`text-sm font-semibold capitalize ${ROLE_TEXT[user.role] || 'text-gray-600'}`}>
                    {user.role}
                  </span>
                </div>
              )}
              <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
            </button>
          )}

        </div>
      </div>

      {/* Portal dropdown — renders directly on body, above everything */}
      {dropdown}

      {/* Change Password Modal */}
      {showChangePwd && (
        <ChangePasswordModal onClose={() => setShowChangePwd(false)} />
      )}
    </header>
  );
};

export default Header;
